import { FC, useEffect, useState } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useSignalRStatus } from './signalRHubConnection';
import { useLocale } from '@app/core/hooks/useLocale';
import styles from './SignalRStatusIndicator.module.scss';
import {
  FeatureFlags,
  getFeatureFlag,
  subscribeToFeatureFlags,
} from '../feature-flags/feature-flags';

const SignalRStatusIndicator: FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scale, setScale] = useState(1);
  const [boxShadow, setBoxShadow] = useState('0 4px 6px rgba(0, 0, 0, 0.3)');
  const [timestamp, setTimestamp] = useState(new Date().toLocaleTimeString());
  const [displayState, setDisplayState] = useState({
    connectionState: HubConnectionState.Disconnected,
    isTabVisible: false,
    hasDisconnectTimer: false,
    activeOperations: {} as Record<string, boolean>,
    isConnectionPaused: false,
  });
  const { l } = useLocale();

  const {
    connectionState,
    isTabVisible,
    hasDisconnectTimer,
    activeOperations,
    isConnectionPaused,
  } = useSignalRStatus();

  useEffect(() => {
    const updateVisibility = () => setIsVisible(getFeatureFlag(FeatureFlags.SignalrMonitor));
    updateVisibility();

    return subscribeToFeatureFlags(updateVisibility);
  }, []);

  useEffect(() => {
    let flashTimer: NodeJS.Timeout | null = null;

    // Delay status update by 2 seconds
    const delayTimer = setTimeout(() => {
      setDisplayState({
        connectionState,
        isTabVisible,
        hasDisconnectTimer,
        activeOperations,
        isConnectionPaused,
      });

      // Trigger flash animation
      setScale(1.05);
      setBoxShadow('0 6px 12px rgba(255, 255, 0, 0.5)');
      setTimestamp(new Date().toLocaleTimeString());

      flashTimer = setTimeout(() => {
        setScale(1);
        setBoxShadow('0 4px 6px rgba(0, 0, 0, 0.3)');
      }, 1000);
    }, 2000);

    return () => {
      clearTimeout(delayTimer);
      if (flashTimer) {
        clearTimeout(flashTimer);
      }
    };
  }, [connectionState, isTabVisible, hasDisconnectTimer, activeOperations, isConnectionPaused]);

  if (!isVisible) {
    return null;
  }

  const activeOpsArray = Object.keys(displayState.activeOperations).filter(
    (k: string) => displayState.activeOperations[k]
  );
  const hasActiveOps = activeOpsArray.length > 0;

  const getStateColorClass = () => {
    if (displayState.connectionState === HubConnectionState.Connected) {
      return styles.connected;
    }
    if (displayState.connectionState === HubConnectionState.Disconnected) {
      return styles.disconnected;
    }
    return styles.warning;
  };

  return (
    <div
      className={styles.statusIndicator}
      style={{
        transform: `scale(${scale})`,
        boxShadow,
      }}>
      <div className={styles.header}>{l('_SignalRStatusMonitor')}</div>

      <div className={styles.timestamp}>
        {l('_LastUpdate')} {timestamp}
      </div>

      <div className={styles.row}>
        <span className={styles.label}>{l('_Connection')} </span>
        <span className={`${styles.value} ${getStateColorClass()}`}>
          {displayState.connectionState}
        </span>
      </div>

      <div className={styles.row}>
        <span className={styles.label}>{l('_TabVisible')} </span>
        <span className={`${styles.value} ${displayState.isTabVisible ? styles.yes : styles.no}`}>
          {l(displayState.isTabVisible ? '_Yes' : '_No')}
        </span>
      </div>

      <div className={styles.row}>
        <span className={styles.label}>{l('_ActiveOperations')} </span>
        <span className={`${styles.value} ${hasActiveOps ? styles.active : styles.none}`}>
          {hasActiveOps ? activeOpsArray.join(', ') : l('_None')}
        </span>
      </div>

      <div className={styles.row}>
        <span className={styles.label}>{l('_DisconnectTimer')} </span>
        <span
          className={`${styles.value} ${
            displayState.hasDisconnectTimer ? styles.active : styles.inactive
          }`}>
          {l(displayState.hasDisconnectTimer ? '_Active' : '_Inactive')}
        </span>
      </div>

      <div className={styles.row}>
        <span className={styles.label}>{l('_Paused')}: </span>
        <span
          className={`${styles.value} ${
            displayState.isConnectionPaused ? styles.active : styles.inactive
          }`}>
          {l(displayState.isConnectionPaused ? '_Yes' : '_No')}
        </span>
      </div>
    </div>
  );
};

export default SignalRStatusIndicator;
